import { useState } from 'react'
import { Typography, Breadcrumb, Card, Form, Select, Empty } from 'antd'
import { EditableTable } from '@/components/EditableTable'
import type { ColumnConfig, ChangeSet } from '@/components/EditableTable/types'
import {
  useDepartments,
  useUpdateDepartment,
  useFilials,
  useServices,
} from '@/hooks/useRefs'
import type { DepartmentFilters } from '@/api/refs'

const { Title } = Typography

export default function DepartmentsPage() {
  const [filters, setFilters] = useState<DepartmentFilters>({
    ot_per: undefined,
    fil_code: undefined,
    otd_prof_type: undefined,
  })

  const { data: departments, isLoading, refetch } = useDepartments(filters)
  const { data: filials } = useFilials()
  const { data: services } = useServices()
  const updateMutation = useUpdateDepartment()

  const filialOptions = filials?.map((f) => ({ value: f.fil_code, label: f.fil_name })) || []
  const serviceOptions = services?.map((s) => ({ value: s.serv_code, label: s.serv_name })) || []

  const columns: ColumnConfig[] = [
    {
      field: 'id_otd',
      title: 'ID отделения',
      type: 'text',
      width: 150,
      editable: false,
    },
    {
      field: 'ot_per',
      title: 'Период',
      type: 'text',
      width: 100,
      editable: false,
    },
    {
      field: 'code_otd_name_full',
      title: 'Название из МИС',
      type: 'text',
      editable: false,
    },
    {
      field: 'fil_code',
      title: 'Филиал',
      type: 'select',
      width: 200,
      editable: true,
      options: filialOptions,
    },
    {
      field: 'serv_name',
      title: 'Служба',
      type: 'select',
      width: 200,
      editable: true,
      options: serviceOptions,
    },
    {
      field: 'otd_prof_type',
      title: 'Тип профиля',
      type: 'text',
      width: 120,
      editable: false,
    },
    {
      field: 'status',
      title: 'Статус',
      type: 'text',
      width: 100,
      editable: false,
    },
  ]

  const handleSave = async (changes: ChangeSet) => {
    if (changes.updated) {
      for (const [key, data] of Object.entries(changes.updated)) {
        // key format: "id_otd:ot_per" or just row index
        const dept = departments?.find(d => `${d.id_otd}:${d.ot_per}` === key || d.id_otd === key)
        if (dept) {
          await updateMutation.mutateAsync({
            id_otd: dept.id_otd,
            ot_per: dept.ot_per,
            data: {
              fil_code: data.fil_code,
              serv_name: data.serv_name,
              otd_prof_type: data.otd_prof_type,
            }
          })
        }
      }
    }
    refetch()
  }

  // Генерация периодов (последние 12 месяцев)
  const periodOptions = []
  const now = new Date()
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const value = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`
    const label = date.toLocaleDateString('ru-RU', { year: 'numeric', month: 'long' })
    periodOptions.push({ value, label })
  }

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>Справочники</Breadcrumb.Item>
        <Breadcrumb.Item>Отделения</Breadcrumb.Item>
      </Breadcrumb>

      <Title level={2}>Отделения (id_recviz_v2)</Title>

      <Card style={{ marginBottom: 16 }}>
        <Form layout="inline">
          <Form.Item label="Период">
            <Select
              style={{ width: 200 }}
              placeholder="Выберите период"
              value={filters.ot_per}
              onChange={(value) => setFilters({ ...filters, ot_per: value })}
              options={periodOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Филиал">
            <Select
              style={{ width: 200 }}
              placeholder="Все филиалы"
              allowClear
              value={filters.fil_code}
              onChange={(value) => setFilters({ ...filters, fil_code: value })}
              options={filialOptions}
            />
          </Form.Item>

          <Form.Item label="Тип профиля">
            <Select
              style={{ width: 150 }}
              placeholder="Все"
              allowClear
              value={filters.otd_prof_type}
              onChange={(value) => setFilters({ ...filters, otd_prof_type: value })}
              options={[
                { value: 'КС', label: 'КС' },
                { value: 'ДС', label: 'ДС' },
              ]}
            />
          </Form.Item>
        </Form>
      </Card>

      {!departments?.length && !isLoading ? (
        <Empty description="Нет данных. Выберите период или запустите ETL для загрузки данных из МИС." />
      ) : (
        <EditableTable
          columns={columns}
          dataSource={departments || []}
          rowKey="id_otd"
          loading={isLoading}
          onSave={handleSave}
          draftKey="departments"
        />
      )}
    </div>
  )
}
